import React, { useState } from 'react';
import { Clock, ExternalLink, MapPin, Network, Trello } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import type { ArtifactViewerProps } from '../types';
import type { KanbanContent, MapContent, MindmapContent, MindmapNode, TimelineContent } from './shared';

export const MapViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<MapContent>;
  const center = Array.isArray(c?.center) && c.center.length === 2 ? c.center : undefined;
  const zoom = typeof c?.zoom === 'number' ? c.zoom : 13;
  const markers = Array.isArray(c?.markers) ? c.markers : [];
  if (!center) return <div className="p-4 text-sm text-muted-foreground">Coordinate mappa mancanti</div>;
  const [lat, lng] = center;
  const delta = Math.max(0.005, 0.5 / Math.max(1, zoom));
  const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted">
        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{lat.toFixed(4)}, {lng.toFixed(4)} · zoom {zoom}</span>
        <a
          href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`}
          target="_blank" rel="noreferrer"
          className="ml-auto text-[11px] text-violet-600 hover:underline inline-flex items-center gap-1"
        >
          Apri <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <iframe
        title={`map-${artifact.slug}`}
        src={src}
        className="w-full h-[320px] border-0"
        loading="lazy"
      />
      {markers.length > 0 && (
        <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground">
          {markers.length} marker
          {markers.slice(0, 5).map((m, i) => (
            <div key={i} className="text-[11px] text-muted-foreground">
              · {m.lat.toFixed(4)}, {m.lng.toFixed(4)}{m.label ? ` — ${m.label}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TimelineViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<TimelineContent>;
  const items = Array.isArray(c?.items) ? c.items : [];
  if (items.length === 0) return <div className="p-4 text-sm text-muted-foreground">Timeline vuota</div>;
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        {items.length} eventi
      </div>
      <ol className="relative border-l-2 border-violet-200 ml-2 space-y-4">
        {items.map((it) => (
          <li key={it.id} className="ml-4">
            <div className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-violet-500 border-2 border-white" />
            <div className="text-[11px] uppercase tracking-wide text-violet-600 font-medium">{it.date}</div>
            <div className="text-sm font-medium text-foreground">{it.title}</div>
            {it.description && <div className="text-xs text-muted-foreground mt-0.5">{it.description}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
};

export const KanbanViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<KanbanContent>;
  const columns = Array.isArray(c?.columns) ? c.columns : [];
  if (columns.length === 0) return <div className="p-4 text-sm text-muted-foreground">Kanban senza colonne</div>;
  return (
    <div className="bg-card rounded-lg border border-border p-3 overflow-x-auto">
      <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
        <Trello className="h-3.5 w-3.5 text-muted-foreground" />
        {columns.length} colonne · {columns.reduce((a, col) => a + (col.cards?.length ?? 0), 0)} card
      </div>
      <div className="flex gap-3 min-w-max">
        {columns.map((col) => (
          <div key={col.id} className="w-60 shrink-0 bg-muted rounded-lg p-2">
            <div className="text-xs font-semibold text-foreground mb-2 px-1 flex items-center justify-between">
              <span>{col.title}</span>
              <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{col.cards?.length ?? 0}</Badge>
            </div>
            <div className="space-y-1.5">
              {(col.cards ?? []).map((card) => (
                <div key={card.id} className="bg-card rounded border border-border p-2 text-xs">
                  <div className="font-medium text-foreground">{card.title}</div>
                  {card.description && <div className="text-muted-foreground mt-1 text-[11px]">{card.description}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MindmapNodeView: React.FC<{ node: MindmapNode; depth: number }> = ({ node, depth }) => {
  const [open, setOpen] = useState(true);
  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  return (
    <div className="text-sm" style={{ marginLeft: depth === 0 ? 0 : 12 }}>
      <button
        type="button"
        onClick={() => hasChildren && setOpen(o => !o)}
        className={`flex items-center gap-1 py-0.5 ${hasChildren ? 'cursor-pointer hover:text-violet-700' : 'cursor-default'}`}
      >
        {hasChildren ? (
          <span className="text-violet-500 text-xs w-3 inline-block">{open ? '▾' : '▸'}</span>
        ) : (
          <span className="text-muted-foreground text-xs w-3 inline-block">•</span>
        )}
        <span className={depth === 0 ? 'font-semibold text-foreground' : 'text-foreground'}>{node.label}</span>
      </button>
      {hasChildren && open && (
        <div className="border-l border-violet-100 pl-2 ml-1.5">
          {children.map((child) => <MindmapNodeView key={child.id} node={child} depth={depth + 1} />)}
        </div>
      )}
    </div>
  );
};

export const MindmapViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<MindmapContent>;
  const root = c?.root;
  if (!root || typeof root.id !== 'string') return <div className="p-4 text-sm text-muted-foreground">Mind map vuota</div>;
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
        <Network className="h-3.5 w-3.5 text-muted-foreground" />
        Mind map
      </div>
      <MindmapNodeView node={root} depth={0} />
    </div>
  );
};
